import { useEffect, useMemo, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/pos-api";
import { rupiah } from "@/lib/format";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useListNav } from "@/lib/useListNav";
import MoneyInput from "@/components/MoneyInput";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Plus, Trash2, Search, ShoppingBag, ChevronRight, PackagePlus, Check, Upload, FileDown, FileSpreadsheet, Loader2,
} from "lucide-react";

const statusBadge = {
  lunas: "bg-emerald-500/15 text-emerald-600",
  utang: "bg-amber-500/15 text-amber-600",
};

export default function Purchases() {
  const [purchases, setPurchases] = useState([]);
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [open, setOpen] = useState(false);
  const [selected, setSelected] = useState(null);
  const [importLines, setImportLines] = useState(null);
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const [importSupplier, setImportSupplier] = useState(null);

  const load = async () => {
    try {
      const [pu, pr, su] = await Promise.all([
        api.get("/purchases"), api.get("/products"), api.get("/suppliers"),
      ]);
      setPurchases(pu.data);
      setProducts(pr.data);
      setSuppliers(su.data);
    } catch (e) { toast.error(apiError(e)); }
  };
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const openId = location.state?.openPurchaseId;
    if (!openId || !purchases.length) return;
    const found = purchases.find((p) => p.id === openId);
    if (found) setSelected(found);
    navigate(location.pathname, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.state, purchases]);

  const setStatus = async (p, status) => {
    try {
      await api.put(`/purchases/${p.id}/payment?status=${status}`);
      toast.success("Status pembayaran diperbarui");
      load();
      setSelected((s) => (s && s.id === p.id ? { ...s, payment_status: status } : s));
    } catch (e) { toast.error(apiError(e)); }
  };

  const downloadFile = async (url, filename) => {
    try {
      const res = await api.get(url, { responseType: "blob" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(res.data);
      link.download = filename;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (e) { toast.error(apiError(e)); }
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (fileRef.current) fileRef.current.value = "";
    if (!file) return;
    setImporting(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/purchases/parse", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (data.errors?.length) toast.warning(`${data.errors.length} baris dilewati (produk tidak ditemukan)`);
      if (!data.items?.length) { toast.error("Tidak ada baris valid pada file Excel"); return; }
      const lines = data.items.map((it) => ({
        product_id: it.product_id, name: it.name, qty: Number(it.qty || 1),
        baseCost: Number(it.cost || 0), cost: Number(it.cost || 0),
        units: it.units || [], baseUnit: it.base_unit || "Pcs", unit: it.base_unit || "Pcs", pieces: 1,
      }));
      setImportLines(lines);
      setOpen(true);
      toast.success(`${lines.length} barang dimuat dari Excel — lengkapi supplier lalu simpan`);
    } catch (err) { toast.error(apiError(err)); }
    finally { setImporting(false); }
  };

  return (
    <>
      <PageHeader title="Pembelian Barang" subtitle={`${purchases.length} faktur pembelian`}>
        <div className="flex items-center gap-2">
          <input ref={fileRef} data-testid="import-file-input" type="file" accept=".xlsx,.xls" onChange={handleImport} className="hidden" />
          <Button data-testid="download-template-btn" variant="outline" onClick={() => downloadFile("/purchases/template", "template_pembelian.xlsx")} className="border-slate-300 bg-transparent hover:bg-[#F7F9FB] gap-2">
            <FileDown className="h-4 w-4" /> <span className="hidden sm:inline">Template</span>
          </Button>
          <Button data-testid="import-excel-btn" variant="outline" onClick={() => fileRef.current?.click()} disabled={importing} className="border-emerald-600/50 bg-transparent hover:bg-emerald-500/10 text-emerald-600 gap-2">
            {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} <span className="hidden sm:inline">Import Excel</span>
          </Button>
          <Button data-testid="export-purchases-btn" variant="outline" onClick={() => downloadFile("/purchases/export", "riwayat_pembelian.xlsx")} className="border-slate-300 bg-transparent hover:bg-[#F7F9FB] gap-2">
            <FileSpreadsheet className="h-4 w-4" /> <span className="hidden sm:inline">Export</span>
          </Button>
          <Button data-testid="add-purchase-btn" onClick={() => { setImportLines(null); setOpen(true); }} className="bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-bold gap-2">
            <Plus className="h-4 w-4" /> <span className="hidden sm:inline">Input Pembelian</span>
          </Button>
        </div>
      </PageHeader>

      <div className="flex-1 overflow-y-auto p-5">
        <div className="rounded-xl border border-slate-200 overflow-hidden" data-testid="purchases-table">
          <table className="w-full text-sm">
            <thead className="bg-[#F7F9FB] text-slate-500 text-xs uppercase tracking-wide">
              <tr>
                <th className="text-left p-3">No. Faktur</th>
                <th className="text-left p-3">Supplier</th>
                <th className="text-left p-3 hidden md:table-cell">Tanggal</th>
                <th className="text-center p-3">Status</th>
                <th className="text-right p-3">Total</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {purchases.map((p) => (
                <tr key={p.id} data-testid={`purchase-row-${p.id}`} onClick={() => setSelected(p)} className="border-t border-slate-200 hover:bg-slate-50 cursor-pointer">
                  <td className="p-3 font-mono">{p.invoice_no}</td>
                  <td className="p-3">{p.supplier_name}</td>
                  <td className="p-3 hidden md:table-cell text-slate-500">{p.date}</td>
                  <td className="p-3 text-center"><span className={`text-xs px-2 py-1 rounded-full capitalize ${statusBadge[p.payment_status]}`}>{p.payment_status}</span></td>
                  <td className="p-3 text-right font-mono font-bold text-blue-400">{rupiah(p.total)}</td>
                  <td className="p-3 text-slate-500"><ChevronRight className="h-4 w-4" /></td>
                </tr>
              ))}
              {purchases.length === 0 && (
                <tr><td colSpan={6} className="p-10 text-center text-slate-500"><ShoppingBag className="h-8 w-8 mx-auto mb-2" /> Belum ada pembelian</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <PurchaseForm open={open} onClose={() => { setOpen(false); setImportLines(null); setImportSupplier(null); }} initialLines={importLines} initialSupplier={importSupplier} products={products} suppliers={suppliers} onSaved={load} />

      {selected && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={() => setSelected(null)}>
          <div className="bg-[#F7F9FB] border border-slate-300 rounded-xl max-w-md w-full p-5 animate-pop" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <div>
                <div className="font-heading font-bold text-lg">{selected.invoice_no}</div>
                <div className="text-xs text-slate-500">{selected.supplier_name} · {selected.date}</div>
              </div>
              <span className={`text-xs px-2 py-1 rounded-full capitalize ${statusBadge[selected.payment_status]}`}>{selected.payment_status}</span>
            </div>
            <div className="space-y-1 border-t border-dashed border-slate-300 pt-3">
              {selected.items.map((it, i) => (
                <div key={i} className="flex justify-between text-sm">
                  <span className="text-slate-600">{it.name} <span className="text-slate-500">x{it.qty} @ {rupiah(it.cost)}</span></span>
                  <span className="font-mono">{rupiah(it.qty * it.cost)}</span>
                </div>
              ))}
            </div>
            {selected.note && <div className="text-xs text-slate-500 mt-3 border-t border-dashed border-slate-300 pt-2">Catatan: {selected.note}</div>}
            <div className="flex justify-between font-bold text-blue-400 mt-3 border-t border-dashed border-slate-300 pt-3">
              <span>Total</span><span className="font-mono">{rupiah(selected.total)}</span>
            </div>
            <div className="grid grid-cols-2 gap-2 mt-4">
              {selected.payment_status === "utang" ? (
                <Button data-testid="mark-lunas-btn" onClick={() => setStatus(selected, "lunas")} className="bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-bold gap-1"><Check className="h-4 w-4" /> Tandai Lunas</Button>
              ) : (
                <Button data-testid="mark-utang-btn" variant="outline" onClick={() => setStatus(selected, "utang")} className="border-slate-300 bg-transparent hover:bg-[#F7F9FB]">Jadikan Utang</Button>
              )}
              <button onClick={() => setSelected(null)} className="h-10 rounded-lg bg-[#F7F9FB] hover:bg-slate-200 font-semibold">Tutup</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

function PurchaseForm({ open, onClose, products, suppliers, onSaved, initialLines, initialSupplier }) {
  const [supplierName, setSupplierName] = useState("");
  const [supplierId, setSupplierId] = useState("");
  const [invoiceNo, setInvoiceNo] = useState("");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [note, setNote] = useState("");
  const [status, setStatus] = useState("lunas");
  const [lines, setLines] = useState([]);
  const [search, setSearch] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      setSupplierName(initialSupplier?.name || ""); setSupplierId(initialSupplier?.id || ""); setInvoiceNo("");
      setDate(new Date().toISOString().slice(0, 10)); setNote("");
      setStatus("lunas"); setLines(initialLines || []); setSearch("");
    }
  }, [open]);

  const addLine = (p) => {
    setLines((prev) => {
      if (prev.find((l) => l.product_id === p.id)) return prev;
      return [...prev, {
        product_id: p.id, name: p.name, qty: 1,
        baseCost: p.cost || 0, cost: p.cost || 0,
        units: p.units || [], baseUnit: p.base_unit || "Pcs", unit: p.base_unit || "Pcs", pieces: 1,
      }];
    });
    setSearch("");
  };
  const updateLineQty = (i, val) =>
    setLines((prev) => prev.map((l, idx) => {
      if (idx !== i) return l;
      const q = Number(val || 0);
      if (l.inputMode === "subtotal") {
        const sub = Number(l.subtotal || 0);
        return { ...l, qty: val, cost: q > 0 ? sub / q : 0 };
      }
      return { ...l, qty: val };
    }));
  const updateLineCost = (i, val) =>
    setLines((prev) => prev.map((l, idx) => (idx === i ? { ...l, cost: val, inputMode: "modal" } : l)));
  const updateLineSubtotal = (i, val) =>
    setLines((prev) => prev.map((l, idx) => {
      if (idx !== i) return l;
      const sub = Number(val || 0);
      const q = Number(l.qty || 0);
      return { ...l, subtotal: val, cost: q > 0 ? sub / q : 0, inputMode: "subtotal" };
    }));
  const setLineMode = (i, mode) =>
    setLines((prev) => prev.map((l, idx) => {
      if (idx !== i) return l;
      if (mode === "subtotal") {
        const sub = Number(l.qty || 0) * Number(l.cost || 0);
        return { ...l, inputMode: "subtotal", subtotal: sub || "" };
      }
      return { ...l, inputMode: "modal" };
    }));
  const setLineUnit = (i, unitName) =>
    setLines((prev) => prev.map((l, idx) => {
      if (idx !== i) return l;
      if (unitName === l.baseUnit) return { ...l, unit: unitName, pieces: 1, cost: l.baseCost };
      const u = (l.units || []).find((x) => x.name === unitName);
      const pieces = u ? Number(u.pieces || 1) : 1;
      return { ...l, unit: unitName, pieces, cost: Number(l.baseCost || 0) * pieces };
    }));
  const removeLine = (i) => setLines((prev) => prev.filter((_, idx) => idx !== i));

  const total = lines.reduce((s, l) => s + Number(l.qty || 0) * Number(l.cost || 0), 0);
  const filtered = useMemo(
    () => (search ? products.filter((p) => p.name.toLowerCase().includes(search.toLowerCase())).slice(0, 6) : []),
    [search, products]
  );
  const pnav = useListNav(filtered, addLine);

  const onPickSupplier = (val) => {
    if (val === "manual") { setSupplierId(""); return; }
    setSupplierId(val);
    const s = suppliers.find((x) => x.id === val);
    if (s) setSupplierName(s.name);
  };

  const save = async () => {
    if (!supplierName.trim()) return toast.error("Nama supplier wajib diisi");
    if (lines.length === 0) return toast.error("Tambahkan minimal 1 barang");
    setSaving(true);
    try {
      await api.post("/purchases", {
        supplier_id: supplierId || null,
        supplier_name: supplierName.trim(),
        invoice_no: invoiceNo.trim() || null,
        date, note: note || null,
        payment_status: status,
        items: lines.map((l) => ({
          product_id: l.product_id, name: l.name,
          qty: Number(l.qty || 0), cost: Number(l.cost || 0),
          unit: l.unit || "Pcs", pieces: Number(l.pieces || 1),
        })),
      });
      toast.success("Pembelian tersimpan, stok & harga modal diperbarui");
      onClose(); onSaved();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent data-testid="purchase-form-dialog" className="bg-[#F7F9FB] border-slate-300 text-slate-800 max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading">Input Pembelian Barang</DialogTitle>
          <DialogDescription className="text-slate-500 text-xs">Stok & harga modal produk diperbarui otomatis saat disimpan.</DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="text-slate-600 text-xs">Supplier</Label>
            <Select value={supplierId || "manual"} onValueChange={onPickSupplier}>
              <SelectTrigger data-testid="purchase-supplier-select" className="bg-[#F7F9FB] border-slate-300"><SelectValue placeholder="Pilih / ketik manual" /></SelectTrigger>
              <SelectContent className="bg-[#F7F9FB] border-slate-300 text-slate-800">
                <SelectItem value="manual">— Ketik Manual —</SelectItem>
                {suppliers.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <Input data-testid="purchase-supplier-name" value={supplierName} onChange={(e) => { setSupplierName(e.target.value); setSupplierId(""); }} placeholder="Nama supplier" className="bg-[#F7F9FB] border-slate-300 mt-1" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label className="text-slate-600 text-xs">No. Faktur (opsional)</Label>
              <Input data-testid="purchase-invoice-no" value={invoiceNo} onChange={(e) => setInvoiceNo(e.target.value)} placeholder="Otomatis" className="bg-[#F7F9FB] border-slate-300 font-mono" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-slate-600 text-xs">Tanggal</Label>
              <Input data-testid="purchase-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="bg-[#F7F9FB] border-slate-300" />
            </div>
          </div>
        </div>

        {/* Product search */}
        <div className="relative mt-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
          <Input data-testid="purchase-product-search" value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={pnav.onInputKeyDown} placeholder="Cari & tambahkan barang…" className="pl-10 bg-[#F7F9FB] border-slate-300" />
          {filtered.length > 0 && (
            <div ref={pnav.listRef} className="absolute z-10 mt-1 w-full rounded-lg border border-slate-300 bg-[#F7F9FB] shadow-xl overflow-hidden">
              {filtered.map((p, idx) => (
                <button key={p.id} data-testid={`purchase-pick-${p.id}`} data-nav-index={idx} onMouseEnter={() => pnav.setActive(idx)} onClick={() => addLine(p)} className={`w-full text-left px-3 py-2 flex items-center justify-between ${idx === pnav.active ? "bg-slate-200" : "hover:bg-slate-200"}`}>
                  <span className="text-sm">{p.name}</span>
                  <span className="text-xs text-slate-500 font-mono">stok {p.stock}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Lines */}
        <div className="rounded-xl border border-slate-200 overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#F7F9FB] text-slate-500 text-xs uppercase">
              <tr>
                <th className="text-left p-2 pl-3">Barang</th>
                <th className="text-center p-2 w-24">Qty</th>
                <th className="text-right p-2 w-44">Harga Modal / Subtotal</th>
                <th className="text-right p-2 w-28">Subtotal</th>
                <th className="w-10"></th>
              </tr>
            </thead>
            <tbody data-testid="purchase-lines">
              {lines.map((l, i) => (
                <tr key={i} className="border-t border-slate-200">
                  <td className="p-2 pl-3">
                    <div>{l.name}</div>
                    {l.units && l.units.length > 0 && (
                      <Select value={l.unit} onValueChange={(v) => setLineUnit(i, v)}>
                        <SelectTrigger data-testid={`purchase-unit-${i}`} className="h-7 mt-1 w-32 bg-slate-100 border-slate-300 text-xs"><SelectValue /></SelectTrigger>
                        <SelectContent className="bg-[#F7F9FB] border-slate-300 text-slate-800">
                          <SelectItem value={l.baseUnit}>{l.baseUnit}</SelectItem>
                          {l.units.map((u, ui) => <SelectItem key={ui} value={u.name}>{u.name} (isi {u.pieces})</SelectItem>)}
                        </SelectContent>
                      </Select>
                    )}
                    {l.pieces > 1 && <div className="text-[10px] text-blue-300 mt-0.5">= {Number(l.qty || 0) * Number(l.pieces)} pcs masuk stok</div>}
                  </td>
                  <td className="p-2">
                    <Input data-testid={`purchase-qty-${i}`} type="number" value={l.qty} onChange={(e) => updateLineQty(i, e.target.value)} className="h-9 text-center font-mono bg-slate-100 border-slate-300" />
                  </td>
                  <td className="p-2">
                    <div className="flex gap-1 mb-1">
                      <button type="button" data-testid={`mode-modal-${i}`} onClick={() => setLineMode(i, "modal")} className={`flex-1 h-6 rounded text-[10px] font-semibold no-select ${(l.inputMode || "modal") === "modal" ? "bg-emerald-500 text-slate-950" : "bg-slate-100 text-slate-500 border border-slate-300"}`}>Modal/satuan</button>
                      <button type="button" data-testid={`mode-subtotal-${i}`} onClick={() => setLineMode(i, "subtotal")} className={`flex-1 h-6 rounded text-[10px] font-semibold no-select ${l.inputMode === "subtotal" ? "bg-emerald-500 text-slate-950" : "bg-slate-100 text-slate-500 border border-slate-300"}`}>Subtotal</button>
                    </div>
                    {l.inputMode === "subtotal" ? (
                      <>
                        <MoneyInput data-testid={`purchase-subtotal-${i}`} value={l.subtotal ?? ""} onValueChange={(v) => updateLineSubtotal(i, v)} placeholder="total 1 baris" className="h-9 text-right font-mono bg-slate-100 border-slate-300" />
                        <div className="text-[10px] text-slate-500 mt-0.5 text-right">modal/satuan: {rupiah(Number(l.cost || 0))}</div>
                      </>
                    ) : (
                      <MoneyInput data-testid={`purchase-cost-${i}`} value={l.cost} onValueChange={(v) => updateLineCost(i, v)} className="h-9 text-right font-mono bg-slate-100 border-slate-300" />
                    )}
                  </td>
                  <td className="p-2 text-right font-mono text-blue-400">{rupiah(Number(l.qty || 0) * Number(l.cost || 0))}</td>
                  <td className="p-2 text-center">
                    <button data-testid={`purchase-remove-${i}`} onClick={() => removeLine(i)} className="text-red-600 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                  </td>
                </tr>
              ))}
              {lines.length === 0 && (
                <tr><td colSpan={5} className="p-6 text-center text-slate-500"><PackagePlus className="h-6 w-6 mx-auto mb-1" /> Belum ada barang</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label className="text-slate-600 text-xs">Status Pembayaran ke Supplier</Label>
            <Select value={status} onValueChange={setStatus}>
              <SelectTrigger data-testid="purchase-status-select" className="bg-[#F7F9FB] border-slate-300"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-[#F7F9FB] border-slate-300 text-slate-800">
                <SelectItem value="lunas">Lunas</SelectItem>
                <SelectItem value="utang">Utang</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-slate-600 text-xs">Catatan</Label>
            <Textarea data-testid="purchase-note" value={note} onChange={(e) => setNote(e.target.value)} className="bg-[#F7F9FB] border-slate-300 min-h-[38px]" rows={1} />
          </div>
        </div>

        <div className="flex items-center justify-between rounded-xl bg-[#F7F9FB] border border-slate-300 px-4 py-3">
          <span className="text-slate-500">Total Pembelian</span>
          <span data-testid="purchase-total" className="font-mono font-extrabold text-2xl text-blue-400">{rupiah(total)}</span>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-slate-300 bg-transparent hover:bg-[#F7F9FB]">Batal</Button>
          <Button data-testid="save-purchase-btn" onClick={save} disabled={saving} className="bg-emerald-500 hover:bg-emerald-600 text-slate-950 font-bold">Simpan Pembelian</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
